import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import { getCoinStore, createCoinPaymentIntent, purchaseCoins } from '../../../Services/User/CoinStore';
import { showToast } from '../../../Components/Toast/toast';
import coinIcon from '../../../assets/images/coin.png';
import heartDecor from '../../../assets/images/coin-store-figma/decor-heart.svg';

import { useGoogleTranslate } from '../../../Hooks/useGoogleTranslate';
import StripePayment from '../Payment/StripePayment';
import CustomButton from '../../../Components/CustomButton';
import './CoinStore.css';

const CoinStore = () => {
  const pageTitle = useGoogleTranslate('Coin Store');
  const loadingLabel = useGoogleTranslate('Loading coin store...');
  const noCoinsLabel = useGoogleTranslate('No coins available');
  const coinsLabel = useGoogleTranslate('Coins');
  const processingLabel = useGoogleTranslate('Processing...');
  const buyLabel = useGoogleTranslate('Buy');

  usePageTitle(pageTitle);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);

  const { data: coins, isLoading } = useQuery({
    queryKey: ['coin-store'],
    queryFn: getCoinStore,
  });

  const handleBuyCoins = async (coinPackage) => {
    try {
      setIsCreatingIntent(true);
      setSelectedPackage(coinPackage);

      // Create payment intent
      const response = await createCoinPaymentIntent(parseFloat(coinPackage.price));

      if (response?.client_secret) {
        setClientSecret(response.client_secret);
        setShowPayment(true);
      } else {
        showToast('Failed to initialize payment. Please try again.', 'error');
        setIsCreatingIntent(false);
      }
    } catch (error) {
      console.error('Error creating payment intent:', error);
      showToast(error?.message || 'Failed to initialize payment', 'error');
      setIsCreatingIntent(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      // Verify payment intent status
      if (paymentIntent.status !== 'succeeded') {
        showToast('Payment not completed. Please try again.', 'error');
        return;
      }

      // Add a small delay to ensure Stripe has fully processed the charge
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Call purchase coins endpoint
      const response = await purchaseCoins(selectedPackage.id, paymentIntent.id);

      if (response?.status) {
        showToast(`Successfully purchased ${selectedPackage.coins} coins!`, 'success');
        // Optionally navigate to profile or stay on coin store
        setTimeout(() => {
          setShowPayment(false);
          setSelectedPackage(null);
          setClientSecret(null);
          setIsCreatingIntent(false);
        }, 500);
      } else {
        showToast(response?.message || 'Purchase failed. Please contact support.', 'error');
      }
    } catch (error) {
      console.error('Error purchasing coins:', error);
      const errorMessage = error?.message || error?.error || 'Purchase failed. Please contact support.';
      showToast(errorMessage, 'error');
    } finally {
      setShowPayment(false);
      setSelectedPackage(null);
      setClientSecret(null);
      setIsCreatingIntent(false);
    }
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    showToast(error?.message || 'Payment failed', 'error');
    setIsCreatingIntent(false);
  };

  const handleBackFromPayment = () => {
    setShowPayment(false);
    setSelectedPackage(null);
    setClientSecret(null);
    setIsCreatingIntent(false);
  };

  const renderLayout = (content) => (
    <section className="coin-store-page">
      <span className="coin-store-decor coin-store-decor-ring-left-top" aria-hidden="true" />
      <span className="coin-store-decor coin-store-decor-ring-left-bottom" aria-hidden="true" />
      <span className="coin-store-decor coin-store-decor-ring-right-mid" aria-hidden="true" />
      <span className="coin-store-decor coin-store-decor-ring-right-top" aria-hidden="true" />
      <img src={heartDecor} alt="" className="coin-store-decor coin-store-decor-heart" aria-hidden="true" />
      <div className="coin-store-hero">
        <Container className="coin-store-container">
          <div className="coin-store-title-wrap">
            <h1 className="coin-store-title">{pageTitle}</h1>
          </div>
        </Container>
      </div>
      <Container className="coin-store-container">
        <div className="coin-store-main">{content}</div>
      </Container>
    </section>
  );

  if (isLoading) {
    return renderLayout(
      <Row>
        <Col xs={12} className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">{loadingLabel}</p>
        </Col>
      </Row>
    );
  }

  if (!coins || coins?.length === 0) {
    return renderLayout(
      <Row>
        <Col xs={12} className="text-center">
          <p className="mt-3">{noCoinsLabel}</p>
        </Col>
      </Row>
    );
  }

  // Show payment screen if payment is initiated
  if (showPayment && selectedPackage && clientSecret) {
    return (
      <StripePayment
        onBack={handleBackFromPayment}
        amount={parseFloat(selectedPackage.price)}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
        clientSecret={clientSecret}
      />
    );
  }

  return renderLayout(
    <Row className="justify-content-center coin-store-grid">
      {coins.map((coinPackage) => (
        <Col key={coinPackage.id} xs={12} sm={6} lg={4} className="mb-4 d-flex coin-col">
          <div className="coin-card">
            <div className="coin-icon-wrapper">
              <img src={coinIcon} alt="Coins" className="coin-image" />
            </div>
            <hr className="blue-line" />

            <h3 className="coin-amount">{coinPackage.coins} {coinsLabel}</h3>

            <CustomButton
              onClick={() => handleBuyCoins(coinPackage)}
              disabled={isCreatingIntent}
              loading={isCreatingIntent && selectedPackage?.id === coinPackage.id}
              className="coin-buy-btn"
            >
              {isCreatingIntent && selectedPackage?.id === coinPackage.id
                ? processingLabel
                : `${buyLabel} $ ${coinPackage.price}`
              }
            </CustomButton>
          </div>
        </Col>
      ))}
    </Row>
  );
};

export default CoinStore;
